import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  'What a season of youth sports actually costs in 2026 — real numbers by sport, plus honest, no-skim ways families cover travel, fees, and gear.';

const FAQS = [
  {
    q: 'How much does a season of youth sports cost on average in 2026?',
    a: "Families spend an average of $1,016 per child on their child's primary sport, per the Aspen Institute's Project Play survey — up 46% over five years. Add travel (about $414 a year on average) and gear, and the real total often runs higher, especially on travel or club teams.",
  },
  {
    q: 'Which youth sports cost the most?',
    a: 'Ice hockey averages about $2,583 a year, driven mostly by travel and equipment. Soccer averages roughly $1,188, basketball about $1,002, baseball about $714, and tackle football about $581. Competitive or elite travel-team costs can run $5,000–$20,000+ a year.',
  },
  {
    q: 'Are there grants or financial aid available for youth sports?',
    a: 'Yes. Organizations like All Kids Play and various local foundations offer need-based grants specifically to help cover registration, gear, and travel costs so kids can keep playing.',
  },
  {
    q: "What's an honest way to fundraise without losing a cut of what's raised?",
    a: "Traditional team fundraisers (car washes and the like) typically net only $1,000–$3,000 for an entire team, and platforms like Snap! Raise are widely reported to keep roughly 20% of what's raised. Sport Fund gives each athlete their own free page where 100% of every donation reaches them — the small platform fee is added on top for the supporter, never taken out of the gift.",
  },
  {
    q: 'Is it better to ask for one big donation or recurring monthly support?',
    a: 'Recurring monthly support tends to be easier to give and easier to sustain than one large ask. Ten supporters giving $15–$20 a month across a season adds up to more than most one-time asks, and it covers ongoing costs — travel, tournament fees — as they come up.',
  },
];

const CostOfYouthSports2026: React.FC = () => {
  usePageMeta('Cost of Youth Sports in 2026', ARTICLE_DESCRIPTION, {
    article: true,
    image: '/resources/athlete-training.jpg',
    breadcrumbs: [{ name: 'Resources', path: '/resources' }, { name: 'Cost of Youth Sports in 2026' }],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  });
  const TOC_SECTIONS = [
    {
      id: "the-sticker-shock-is-real-and-it-s-rising-fast",
      text: "The sticker shock is real — and it's rising fast"
    },
    {
      id: "where-the-money-actually-goes",
      text: "Where the money actually goes"
    },
    {
      id: "it-s-straining-household-budgets-and-that-matters",
      text: "It's straining household budgets — and that matters"
    },
    {
      id: "honest-ways-families-cover-the-gap",
      text: "Honest ways families cover the gap"
    },
    {
      id: "where-sport-fund-fits",
      text: "Where Sport Fund fits"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        How much does a season of youth sports really cost in 2026 — and honest ways families cover it
      </h1>
      <p className="text-gray-500 mb-8 text-lg">
        Fees, travel, gear, tournaments — the real 2026 numbers, and practical ways families cover them without going
        into debt.
      </p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <h2 id="the-sticker-shock-is-real-and-it-s-rising-fast">The sticker shock is real — and it's rising fast</h2>
        <p>
          If it feels like every season costs more than the last, it's not your imagination. Families' spending on
          their child's primary sport jumped <strong>46% over five years</strong>, reaching an average of{' '}
          <strong>$1,016 per child in 2024</strong>, according to the Aspen Institute's Project Play survey. Add a
          second sport and the average climbs by roughly another $475. Across the country, families now pour an
          estimated <strong>$40 billion a year</strong> into youth sports.
        </p>
        <p>
          Those are averages. The moment a kid moves onto a travel or club team, the numbers change character entirely.
          The Washington Post summed up the new normal bluntly in early 2026: <strong>"$50 to try out, $3,000 to
          play."</strong>
        </p>

        <h2 id="where-the-money-actually-goes">Where the money actually goes</h2>
        <p>A single season is really a stack of line items, and most of them don't show up on the registration form:</p>
        <ul>
          <li>
            <strong>Registration &amp; club fees</strong> — the entry ticket, often several hundred dollars before
            anyone laces up.
          </li>
          <li>
            <strong>Travel &amp; lodging</strong> — the average family spends about <strong>$414 a year</strong> on
            travel, but for travel-team families that figure explodes. Travel baseball families report{' '}
            <strong>$3,000–$5,000 a year on travel and lodging alone.</strong>
          </li>
          <li>
            <strong>Equipment &amp; uniforms</strong> — replaced as kids grow, sometimes twice a season.
          </li>
          <li>
            <strong>Tournament &amp; meet entry fees</strong> — per-event, and they add up across a fall or spring
            calendar.
          </li>
          <li>
            <strong>Coaching, camps &amp; private training</strong> — the fastest-growing bucket for competitive
            athletes.
          </li>
        </ul>
        <p>
          By sport, the spread is wide. Recent survey data puts average annual spend at roughly{' '}
          <strong>$1,188 for soccer</strong>, <strong>$1,002 for basketball</strong>, <strong>$714 for baseball</strong>,
          and <strong>$581 for tackle football</strong> — while <strong>ice hockey averages about $2,583 a year</strong>,
          driven mostly by travel and equipment. On the competitive/elite track, total annual costs commonly run{' '}
          <strong>$5,000 to $20,000+</strong>.
        </p>

        <h2 id="it-s-straining-household-budgets-and-that-matters">It's straining household budgets — and that matters</h2>
        <p>
          This isn't just an inconvenience. In New York Life's Wealth Watch survey,{' '}
          <strong>nearly 1 in 5 parents (about 20%) said they'd gone into debt</strong> to afford their kids' sports,
          and <strong>21% had considered pulling their child out</strong> because of cost. One in four had{' '}
          <strong>dipped into emergency savings</strong>, and roughly one in five had{' '}
          <strong>deprioritized long-term goals like retirement</strong>. More than three-quarters of parents (76%) had
          taken some action to cope — usually cutting spending elsewhere (38%), fundraising (29%), or volunteering to
          offset costs (21%).
        </p>
        <p>Cost is quietly becoming the thing that decides who gets to keep competing. That's the problem worth solving.</p>

        <h2 id="honest-ways-families-cover-the-gap">Honest ways families cover the gap</h2>
        <p>There's no magic here, but there are practical, no-gimmick options:</p>
        <ol>
          <li>
            <strong>Budget the whole season up front, not month by month.</strong> Add every line item — fees, travel,
            gear, tournaments, camps — before the season starts so nothing blindsides you mid-year. (NerdWallet's
            youth-sports budgeting guide is a good template.)
          </li>
          <li>
            <strong>Look for need-based grants.</strong> Organizations like All Kids Play and various local foundations
            offer youth-sports grants specifically to keep kids on the field.
          </li>
          <li>
            <strong>Share the cost with the people who already believe in your athlete.</strong> Grandparents, aunts and
            uncles, family friends, and neighbors often <em>want</em> to chip in — they just need an easy, trustworthy
            way to do it. Team car-wash-style fundraisers typically net only <strong>$1,000–$3,000 for a whole team</strong>,
            and two-thirds of parents don't fundraise at all — largely because the tools are clunky, team-owned, or take
            a cut.
          </li>
          <li>
            <strong>Make support recurring, not one-and-done.</strong> A single $500 ask is hard. Ten people giving
            $15–$20 a month across a season is easy, sustainable, and adds up to more.
          </li>
        </ol>

        <h2 id="where-sport-fund-fits">Where Sport Fund fits</h2>
        <p>
          This is exactly why we built Sport Fund — an individual athlete's own fund page, free to set up, where friends
          and family can give once or <strong>every month of the season.</strong> A few things we're deliberate about:
        </p>
        <ul>
          <li>
            <strong>The athlete (or their family) owns the page</strong> — not a club, not a platform. Payouts go
            straight to your family's bank.
          </li>
          <li>
            <strong>You keep 100% of every donation.</strong> The small platform fee is added on top for the supporter
            at checkout — it is <strong>never</strong> taken out of what your athlete raises. (We show it up front. No
            forced tips, no surprise charges.)
          </li>
          <li>
            <strong>Recurring by design</strong>, so a season's worth of small monthly gifts can quietly cover the fees,
            the gas, the hotel nights, and the entry costs.
          </li>
        </ul>
        <p>
          You can't make youth sports cheap. But you can make the cost easier to carry, and you can make sure the people
          who believe in your athlete have an honest way to help.
        </p>
        <p>
          <Link to="/signup-creator" className="text-blue-600">Start a free fund page</Link> — free for athletes, and
          they keep 100% of every donation.
        </p>
        <p className="text-sm text-gray-500">
          Sources: Aspen Institute Project Play (2025 survey); Kiplinger; Jersey Watch; The Washington Post (Jan 2026);
          New York Life Wealth Watch; NerdWallet; All Kids Play. Figures are averages and vary widely by sport, region,
          and competition level.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/smart-fundraising-guide" className="text-blue-600">
            Smart Fundraising: Strategies That Actually Work for Athletes
          </Link>{' '}
          — set realistic targets, write updates that get shared, and build momentum toward a season goal.
        </p>
        <p>
          <Link to="/resources/how-to-set-a-fundraising-goal" className="text-blue-600">
            How to Set a Fundraising Goal for Your Sports Season
          </Link>{' '}
          — turn these numbers into a goal you can actually share.
        </p>
        <ArticleFeedback articleSlug="cost-of-youth-sports-2026" />
      </article>
    </div>
  );
};

export default CostOfYouthSports2026;
